import { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  fetchFiatBankAccounts,
  fetchPostDeleteFiatBankAccount,
  type FiatBankAccountItem,
} from '@shared-runtime/modules/fiat';
import { useAuth } from '@shared-web/modules/auth/hooks/useAuth';

import {
  API_BANK_NETWORK_CURRENCY,
  CURRENCY,
  FIAT_BANK_ACCOUNT_CAP,
} from '../types/constants';

export function useFiatAccounts() {
  const { isLogin } = useAuth();

  // 注意：当前硬编码为企业用户（isRetailUser = false）
  // 待用户模块实现后，需要从 useUser() 获取 userInfo.isRetailUser
  const isRetailUser = false;

  const {
    data: fiatAccounts,
    isLoading,
    refetch: getFiatAccounts,
    ...rest
  } = useQuery({
    queryKey: ['fiat-accounts'],
    queryFn: () => fetchFiatBankAccounts({}),
    enabled: isLogin,
    staleTime: 30 * 1000,
  });

  const deleteAccountMutation = useMutation({
    mutationFn: (accountId: string) =>
      fetchPostDeleteFiatBankAccount(accountId),
    onSuccess: () => {
      toast.success('Bank account deleted successfully');
      getFiatAccounts();
    },
    onError: (error: any) => {
      // 注意：未实现 customErrorCodes 映射（原版本有特定错误码映射）
      toast.error(error.message || 'Failed to delete bank account');
    },
  });

  const validBankAccounts = useMemo(
    () => fiatAccounts?.validBankAccounts || [],
    [fiatAccounts?.validBankAccounts],
  );

  const validAccountLengthRetail = useMemo(
    () => validBankAccounts.length,
    [validBankAccounts],
  );

  const validAccountLength = useMemo(() => {
    const length: Record<CURRENCY, number> = [
      CURRENCY.USD,
      CURRENCY.EUR,
      CURRENCY.GBP,
      CURRENCY.AED,
    ].reduce(
      (acc, currency) => {
        acc[currency] = validBankAccounts.filter(
          (account: FiatBankAccountItem) =>
            API_BANK_NETWORK_CURRENCY[account.paymentNetwork] === currency,
        ).length;
        return acc;
      },
      {
        [CURRENCY.USD]: 0,
        [CURRENCY.EUR]: 0,
        [CURRENCY.GBP]: 0,
        [CURRENCY.AED]: 0,
      },
    );

    return length;
  }, [validBankAccounts]);

  const disableAddAccount = useMemo(() => {
    if (isRetailUser) {
      // 零售用户：所有货币共享账户数限制
      return {
        [CURRENCY.USD]: validAccountLengthRetail >= FIAT_BANK_ACCOUNT_CAP,
        [CURRENCY.EUR]: validAccountLengthRetail >= FIAT_BANK_ACCOUNT_CAP,
        [CURRENCY.GBP]: validAccountLengthRetail >= FIAT_BANK_ACCOUNT_CAP,
        [CURRENCY.AED]: validAccountLengthRetail >= FIAT_BANK_ACCOUNT_CAP,
      };
    } else {
      // 企业用户：每种货币独立限制
      return {
        [CURRENCY.USD]:
          validAccountLength[CURRENCY.USD] >= FIAT_BANK_ACCOUNT_CAP,
        [CURRENCY.EUR]:
          validAccountLength[CURRENCY.EUR] >= FIAT_BANK_ACCOUNT_CAP,
        [CURRENCY.GBP]:
          validAccountLength[CURRENCY.GBP] >= FIAT_BANK_ACCOUNT_CAP,
        [CURRENCY.AED]:
          validAccountLength[CURRENCY.AED] >= FIAT_BANK_ACCOUNT_CAP,
      };
    }
  }, [isRetailUser, validAccountLengthRetail, validAccountLength]);

  /**
   * Get bank accounts by currency and payment network
   * Checks if account's supportedPaymentNetworks includes the specified network
   */
  const getBankAccountsByCurrencyAndNetwork = useMemo(() => {
    return (currency: string, paymentNetwork?: string) => {
      if (!fiatAccounts?.fiatBankAccounts) return [];

      return fiatAccounts.fiatBankAccounts.filter((account) => {
        const currencyMatch = account.currency === currency;
        if (!paymentNetwork) return currencyMatch;

        // Check if the account supports the specified payment network
        return (
          currencyMatch &&
          account.supportedPaymentNetworks?.includes(paymentNetwork)
        );
      });
    };
  }, [fiatAccounts?.fiatBankAccounts]);

  return {
    fiatAccounts: fiatAccounts || {
      fiatBankAccounts: [],
      validBankAccounts: [],
    },
    loading: isLoading,
    validBankAccounts,
    validAccountLengthRetail,
    validAccountLength,
    disableAddAccount,
    getFiatAccounts,
    getBankAccountsByCurrencyAndNetwork,
    deleteAccount: deleteAccountMutation.mutate,
    deleteAccountLoading: deleteAccountMutation.isPending,
    deleteAccountRes: deleteAccountMutation.data,
    ...rest,
  };
}
